import { useQuery } from '@powersync/react';
import { useRouter } from 'expo-router';
import { useState } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedRef } from 'react-native-reanimated';

import { NewTemplateSheet } from '@/components/domain/NewTemplateSheet';
import { SyncStatusArea } from '@/components/domain/syncStatus/SyncStatusArea';
import { TemplateEmptyState } from '@/components/domain/TemplateEmptyState';
import { TemplateRow } from '@/components/domain/templateRow/TemplateRow';
import { Bounded } from '@/components/ui/Bounded';
import { BoundedScrollView } from '@/components/ui/BoundedScrollView';
import { NavBar } from '@/components/ui/NavBar';
import { Screen } from '@/components/ui/Screen';
import { useAuth } from '@/lib/auth';
import { useUsage } from '@/lib/billing';
import { timeAgo } from '@/lib/format';
import { useGoBack } from '@/lib/navigation';

type TemplateRowData = {
  id: string;
  owner_id: string;
  title: string;
  icon: string | null;
  step_count: number;
  last_run_at: string | null;
};

export default function TemplatesScreen() {
  const router = useRouter();
  const goBack = useGoBack('/');
  const syncScrollRef = useAnimatedRef<Animated.ScrollView>();
  const { session } = useAuth();
  const userId = session?.user.id;
  const usage = useUsage();

  const [newSheetOpen, setNewSheetOpen] = useState(false);

  const { data: templates, isLoading } = useQuery<TemplateRowData>(
    `SELECT t.id, t.owner_id, t.title, t.icon,
        (SELECT COUNT(*) FROM template_items ti WHERE ti.template_id = t.id) AS step_count,
        (SELECT MAX(i.started_at) FROM instances i WHERE i.template_id = t.id) AS last_run_at
      FROM templates t
      WHERE t.deleted_at IS NULL
      ORDER BY MAX(COALESCE(last_run_at, t.updated_at), t.updated_at) DESC`,
  );

  function newTemplate() {
    if (usage.atTemplateLimit) {
      router.push('/paywall');
      return;
    }
    setNewSheetOpen(true);
  }

  return (
    <Screen edges={['top']}>
      <NavBar onBack={goBack} title="Templates" />

      <SyncStatusArea scrollRef={syncScrollRef}>
        {!isLoading && templates.length === 0 ? (
          <Bounded className="flex-1 justify-center px-5">
            <TemplateEmptyState onNewTemplate={newTemplate} />
          </Bounded>
        ) : (
          <BoundedScrollView
            scrollRef={syncScrollRef}
            alwaysBounceVertical
            contentContainerClassName="gap-3 px-5 pb-10 pt-2"
          >
            {templates.map((t) => (
              <TemplateRow
                key={t.id}
                icon={t.icon}
                title={t.title}
                stepCount={t.step_count}
                meta={t.last_run_at ? `run ${timeAgo(t.last_run_at)}` : 'never run'}
                shared={t.owner_id !== userId}
                href={`/template/${t.id}`}
              />
            ))}
          </BoundedScrollView>
        )}
      </SyncStatusArea>

      <NewTemplateSheet visible={newSheetOpen} onClose={() => setNewSheetOpen(false)} />
    </Screen>
  );
}
